import crypto from 'crypto';

enum sourceTypes {
  YELLOW_BACKOFFICE = 'YBO',
  BACKOFFICE = 'BO3',
}

type ParsedPath = { id: string; slug?: string; categories?: string; lang?: string };

/**
 * O(n) tail scan — no RegExp. True when the path ends with exactly 5–6 digits, preceded by `/` or `-`
 * (bare id segment or slug-…-id). Avoids `(?:/|^)(\d{5,6})$` which misses slug-style tails like …-382826.
 */
export function pathLooksLikeLegacyEventSeries(path: string): boolean {
  if (path.length < 6) {
    return false;
  }
  let i = path.length;
  if (path[i - 1] === '/') {
    i--;
  }
  let digits = 0;
  while (i > 0) {
    const c = path.charCodeAt(i - 1);
    if (c >= 48 && c <= 57) {
      digits++;
      i--;
      continue;
    }
    break;
  }
  if (digits < 5 || digits > 6) {
    return false;
  }
  if (i === 0) {
    return true;
  }
  const prev = path.charCodeAt(i - 1);
  return prev === 47 || prev === 45;
}

/** Compiled once — avoids per-request RegExp allocation and helps V8 optimize the hot path. */
const POSSIBLE_LEGACY_EVENT_ID_RE = /(\/[a-z0-9-]+|concert=|\/event\/|^\/)(100\d{5}|\d{6})/;

/** Same pattern as legacy series/event path parser; hoisted for hot middleware path. */
const LEGACY_EVENT_SERIES_PATH_RE
  = /^\/(?:(?<lang>est|rus|eng|fin|lat|lit|lv|lt|en|fi|ru)\/)?(?:(?:piletid|tickets|liput|renginiai|biletes|bileti|bilietai)(?<categories>\/(?:[a-z0-9-]+\/)*)?(?<slug>[a-z0-9-]+)-)?(?<id>\d{5,6})\/?$/;

export const possibleLegacyEventId = (string: string) => {
  // Will match such types of events legacy urls (https://regex101.com/r/3MbEC2/1):
  // https://test.piletilevi.ee/est/piletid/muusika/rock-ja-pop/livenation-test-show-382826/?shop_provider=rcmdEstEng
  // https://store.piletilevi.test.helmes.ee/public/et/event/382826/purchase/sector/73133?sp=rcmdEstEng
  // https://store.piletilevi.test.helmes.ee/public/?concert=382818&shop_provider=rcmdEstEng&design=piletilevi&lang=est&center=7
  // https://www.piletilevi.ee/382826
  const match = string.match(POSSIBLE_LEGACY_EVENT_ID_RE);

  if (!match) {
    return;
  }
  return match[2];
};

export const parseLegacyEventSeriesPath = (string: string): ParsedPath | undefined => {
  // Will match such types of shows
  // https://www.staging.piletilevi.ee/rus/tickets/muusika/rock-ja-pop/zaz-organique-tour-82295/
  // https://www.piletilevi.ee/82295

  // Or:
  // https://www.piletilevi.ee/est/piletid/muusika/koorimuusika/xxviii-laulu-ja-xxi-tantsupidu-iseoma-the-xxviii-song-and-xxi-dance-celebration-iseoma-kinship-95428/
  // https://www.piletilevi.ee/eng/tickets/muusika/koorimuusika/xxviii-laulu-ja-xxi-tantsupidu-iseoma-the-xxviii-song-and-xxi-dance-celebration-iseoma-kinship-95428/
  // https://www.piletilevi.ee/fin/liput/muusika/koorimuusika/xxviii-laulu-ja-xxi-tantsupidu-iseoma-the-xxviii-song-and-xxi-dance-celebration-iseoma-kinship-95428/

  const path = string.split('?', 1)[0];
  // Cheap guard: full regex is expensive on high "miss" traffic (linux perf: ~88% in this RegExp).
  if (!pathLooksLikeLegacyEventSeries(path)) {
    return;
  }

  const match = path.match(LEGACY_EVENT_SERIES_PATH_RE);

  if (!match || !match.groups) {
    return;
  }

  return match.groups as ParsedPath;
};

export const convertLegacyEventId = async (string: string) => {
  const possibleId = string.startsWith('/') ? possibleLegacyEventId(string) : string;

  if (!possibleId || possibleId.length == 5) {
    // Legacy portal series id is 5 characters long
    return;
  }

  const sourceType = possibleId.length === 6 ? sourceTypes.YELLOW_BACKOFFICE : sourceTypes.BACKOFFICE;
  const input = `${sourceType}|${possibleId}`;
  return await hashString(input);
};

const hashString = async (string: string) => {
  let arrayBuffer: Uint8Array<ArrayBuffer> | Buffer<ArrayBufferLike> | '';
  try {
    if (import.meta.client) {
      arrayBuffer = await hash256String(string);
    } else {
      arrayBuffer = crypto.createHash('sha256').update(string, 'utf-8').digest();
    }
    return arrayBuffer ? base32Encode(arrayBuffer).substring(0, 10).toUpperCase() : '';
  } catch (e) {
    console.error('Error generating ID', e);
    return '';
  }
};

export const convertLegacySeriesId = async (id: string) => {
  // TODO: Find proper way to detect legacy series ids
  const sourceType = id.length === 5 || (id.length === 6 && id.startsWith('1')) ? sourceTypes.YELLOW_BACKOFFICE : sourceTypes.BACKOFFICE;
  const input = `${sourceType}|${id}`;
  return await hashString(input);
};
